import React, { useState } from 'react';
import { CloudRain, HeartCrack, Sparkles, Compass, MessageSquareHeart, HandHeart } from 'lucide-react';
import { imageUrl } from '../config';
import { useBlock } from './SiteContent';

const VISUALS = [
  { icon: CloudRain, img: 'service_anxiety' },
  { icon: HeartCrack, img: 'service_depression' },
  { icon: Sparkles, img: 'service_selfworth' },
  { icon: MessageSquareHeart, img: 'service_relationships' },
  { icon: Compass, img: 'service_crisis' },
  { icon: HandHeart, img: 'service_grief' },
];

const DEFAULT = {
  items: [
    { id: 'anxiety',      name: 'Тревожность и панические атаки', desc: 'Когда мысли не дают покоя, тело реагирует первым, а будущее кажется небезопасным. Учимся возвращать опору, спокойствие и ощущение контроля над своей жизнью.' },
    { id: 'relationships',name: 'Эмоциональная зависимость и разрушающие отношения', desc: 'Когда вы теряете себя рядом с другим, живёте в ожидании и терпите то, что раньше никогда бы не приняли. Помогаю честно посмотреть на сценарий, вернуть самоценность и выстроить здоровые границы.' },
    { id: 'selfworth',    name: 'Самооценка и чувство «я недостаточен»', desc: 'Внутренний критик, страх ошибки, ощущение, что вы «не дотягиваете». Учимся слышать свой настоящий голос, отделять его от навязанных установок и опираться на собственную ценность.' },
    { id: 'self',         name: 'Восстановление контакта с собой', desc: 'Это про честность. Про смелость увидеть свою жизнь без иллюзий и встретиться с тем, что вы на самом деле чувствуете. Помогаю услышать свой настоящий голос и вернуть себе право быть собой.' },
    { id: 'crisis',       name: 'Проживание сложных жизненных этапов', desc: 'Развод, предательство, потеря, чувство одиночества и пустоты. Помогаю прожить эту боль, не разрушая себя, и найти в этом опыте не только страдание, но и точку роста.' },
    { id: 'soma',         name: 'Психосоматика и работа с телом', desc: 'Когда тело говорит о том, что душа пока не может выразить словами: зажимы, хроническое напряжение, потеря чувствительности. Через телесные практики возвращаем контакт с собой.' },
  ],
};

function ServiceCard({ s, visual, i }) {
  const [loaded, setLoaded] = useState(false);
  const Icon = visual.icon;
  return (
    <article
      className="reveal group bg-cream border border-ink/8 hover:border-wine/40 transition-all duration-300 hover:-translate-y-0.5 flex flex-col"
      style={{ transitionDelay: `${i * 50}ms` }}
      data-testid={`service-card-${i}`}
    >
      <div className="relative aspect-[16/10] bg-oat overflow-hidden">
        <img
          src={imageUrl(visual.img)}
          alt={s.name}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03] ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-wine/40">
            <Icon size={40} strokeWidth={1.2} />
          </div>
        )}
      </div>
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-3">
          <Icon size={18} strokeWidth={1.5} className="text-wine mt-1 shrink-0" />
          <h3 className="font-heading text-xl md:text-[22px] text-ink leading-tight">
            {s.name}
          </h3>
        </div>
        <p className="text-base md:text-[15px] text-ink/70 leading-relaxed">{s.desc}</p>
      </div>
    </article>
  );
}

export default function Services() {
  const block = useBlock('services', DEFAULT);
  const items = block.items?.length ? block.items : DEFAULT.items;
  return (
    <section id="services" className="py-16 md:py-24 bg-cream" data-testid="services">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="reveal text-center mb-12 md:mb-14">
          <div className="flex items-center justify-center gap-3 mb-4 text-[12px] tracking-[0.24em] uppercase text-wine/70">
            <span className="gold-divider" />
            <span>направления работы</span>
            <span className="gold-divider" />
          </div>
          <h2 className="font-heading text-3xl md:text-[44px] text-ink leading-tight">
            В чём я могу&nbsp;помочь
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((s, i) => (
            <ServiceCard key={s.id || i} s={s} visual={VISUALS[i % VISUALS.length]} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
